import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Shield, Users, Palette, BadgeEuro } from "lucide-react";

const accent = "#D9B8CE";
const green = "#43BD98";

const icons = [
	<Shield size={28} strokeWidth={1.6} />,
	<Users size={28} strokeWidth={1.6} />,
	<Palette size={28} strokeWidth={1.6} />,
	<BadgeEuro size={28} strokeWidth={1.6} />,
];

const staggerContainer = {
	hidden: {},
	show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const cardItem = {
	hidden: { opacity: 0, y: 25 },
	show: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] },
	},
};

export default function Brand2() {
	const { t } = useTranslation();

	const points = Array.from({ length: 4 }, (_, i) => ({
		icon: icons[i],
		title: t(`salon2.brand.points.${i}.title`),
		text: t(`salon2.brand.points.${i}.text`),
	}));

	return (
		<section
			id="marca"
			className="py-24 overflow-hidden"
			style={{
				background:
					"linear-gradient(180deg, #fff9fc 0%, #f9f2f6 40%, #fff9fc 100%)",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div className="max-w-7xl mx-auto px-6 lg:px-16">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, ease: "easeOut" }}
					viewport={{ once: true }}
					className="text-center mb-16"
				>
					<p
						className="uppercase tracking-wider text-sm font-semibold mb-3"
						style={{ color: green }}
					>
						{t("salon2.brand.badge")}
					</p>
					<h2
						className="mb-4"
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.8rem)",
							fontWeight: 800,
							color: "#222",
						}}
					>
						{t("salon2.brand.title.line1")}{" "}
						<span style={{ color: accent }}>{t("salon2.brand.title.highlight")}</span>
					</h2>
					<p
						className="max-w-2xl mx-auto"
						style={{ color: "#666", fontSize: "1.05rem", lineHeight: 1.7 }}
					>
						{t("salon2.brand.subtitle")}
					</p>
				</motion.div>

				{/* 2x2 Grid */}
				<motion.div
					variants={staggerContainer}
					initial="hidden"
					whileInView="show"
					viewport={{ once: true, amount: 0.15 }}
					className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
				>
					{points.map((item, i) => (
						<motion.div
							key={i}
							variants={cardItem}
							whileHover={{
								y: -4,
								boxShadow: "0 16px 40px rgba(217,184,206,0.15)",
								transition: { duration: 0.3 },
							}}
							className="flex gap-5 bg-white rounded-2xl p-7 border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-300"
						>
							<div
								className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
								style={{
									backgroundColor: "rgba(67,189,152,0.1)",
									color: green,
								}}
							>
								{item.icon}
							</div>
							<div>
								<h3
									className="font-semibold text-lg mb-1.5"
									style={{ color: "#222" }}
								>
									{item.title}
								</h3>
								<p
									className="leading-relaxed"
									style={{ color: "#555", fontSize: "0.95rem" }}
								>
									{item.text}
								</p>
							</div>
						</motion.div>
					))}
				</motion.div>
			</div>
		</section>
	);
}
